// The Stats tiles' live adapter (Track 2, T2-4 "Stats-1" — the player's OWN values): app-api's
// `/player-seasons`, `/player-current-league`, `/competitions`, `/player-competitions` and
// `POST /player-stats` → the same ProtoPlayerStats the snapshot hook returns, plus which league
// group the numbers come from. The comparison bars (the group) come from the nightly Stats files:
// `stats-files.ts` (T2-12b).
//
// The competition dropdown (live only — the snapshot holds one league per season): her
// competitions that season, her league first and the default, "All" last. Her numbers follow it,
// and so does the group, like for like (Ben, 2026-10-12, Q-003).

import type {
  CompetitionsResponse,
  PlayerCompetition,
  PlayerCompetitionsResponse,
  PlayerCurrentLeague,
  PlayerSeasonsResponse,
  PlayerStatsRequest,
  PlayerStatsResponse,
  PlayerStatsRow,
  ProtoMetricCategory,
  ProtoPlayerMetric,
  ProtoPlayerStats,
} from "@catbento/shared";
import type { CompetitionId } from "../data/league.js";
import type { PlayerId } from "../data/players.js";
import type { LiveApi } from "./api.js";
import { positionTemplateOf } from "./bio.js";
import { shortCompetitionName } from "./competition-names.js";
import { leagueChoices } from "./league.js";

/**
 * Snapshot metric keys the engine knows under another name — 3 of the snapshot's 162 (checked
 * against engine v1.6.0, 2026-10-07); every other key is the engine's own (track2-plan §8.2 G).
 * - `psaa_90` ("pass success above average") is the engine's `psax_90` (McCabe 25/26 = 2.527 on both).
 * - `opponent_shots_90` / `passes_into_box_90`: renamed by the engine in v1.5 (T2-4a). The
 *   snapshot's values are the SUCCESSFUL passes into the box and non-penalty opponent shots —
 *   McCabe 25/26: 53 and 116 in the DB, the snapshot's numbers. (The engine's
 *   `passes_into_box` no longer exists as a metric; its stored key now counts every attempt.)
 */
const ENGINE_KEY: Record<string, string> = {
  psaa_90: "psax_90",
  opponent_shots_90: "opponent_np_shots_90",
  passes_into_box_90: "passes_into_box_successful_90",
};

/** The Total mode multiplies a per-90 value by this (stats-filter-kit `resolveTotalValue`). */
const NINETIES = "nineties_played";

/**
 * Metrics shown as "—" on ?live=1 because the engine's own numbers are wrong (decided 2026-10-07).
 * `claim_success_perc`: since upstream `fe0182c` its top (Collected + Success/Claim) is no longer
 * part of its bottom (outcome "Claim" only), so it can pass 100% (e.g. 350%). Engine v1.7.0
 * deleted it; Stats now shows its two replacements instead (`liveStatsCategory`, Q-015 B), the
 * Radar only Claimable Claim Success % (`liveRadarKeys`, as Catalyst). It stays here for the modules still on the snapshot's list (Filter,
 * Comparison): "—", and
 * never asked of the engine (packages/soccer-aggregates/README.md "Known upstream issues").
 */
const HIDDEN_LIVE = new Set<string>(["claim_success_perc"]);

/** True for a metric whose engine value is wrong — shown "—", and no comparison bar either
 *  (the group's values are just as wrong). */
export function isHiddenLive(snapshotKey: string): boolean {
  return HIDDEN_LIVE.has(snapshotKey);
}

export function engineKeyOf(snapshotKey: string): string {
  return ENGINE_KEY[snapshotKey] ?? snapshotKey;
}

const CLAIM_SUCCESS = "claimable_claim_success_perc";
const CCAX = "ccax_perc";

/**
 * Q-015 B (Ben, 2026-10-15): on ?live=1 the Stats Goalkeeping tile's Claim Success % row becomes
 * engine v1.7.0's two keeper stats, under their own names — they mean something different from
 * the snapshot's number, so they don't borrow its name. Names + ⓘ text are the engine's.
 */
const LIVE_CLAIM_METRICS: ProtoPlayerMetric[] = [
  {
    key: CLAIM_SUCCESS,
    name: "Claimable Claim Success %",
    shortName: "Claimable Claim Success %",
    outputType: "percentage",
    definition: "Percentage of claimable-pass collection attempts that succeed",
  },
  {
    key: CCAX,
    name: "CCAX %",
    shortName: "CCAX %",
    outputType: "percentage",
    definition:
      "Claim attempts above expectation per claimable pass faced — how much more or less often the goalkeeper attempts a claim than the model expects, independent of whether the attempt succeeds",
  },
];

/** A category on ?live=1: the snapshot's, with `claim_success_perc` swapped for the two new rows.
 *  Stats, Ranking and the Radar / Similarity catalog use it — Filter / Comparison still read the
 *  snapshot list (their claim row stays "—" via HIDDEN_LIVE). Pure; other categories as-is. */
export function liveStatsCategory(category: ProtoMetricCategory): ProtoMetricCategory {
  const swap = (m: ProtoPlayerMetric) => (m.key === "claim_success_perc" ? LIVE_CLAIM_METRICS : [m]);
  if (!category.subcategories.some((s) => s.playerMetrics.some((m) => m.key === "claim_success_perc"))) return category;
  return { ...category, subcategories: category.subcategories.map((s) => ({ ...s, playerMetrics: s.playerMetrics.flatMap(swap) })) };
}

/** The Radar's axis keys on ?live=1: `claim_success_perc` → Claimable Claim Success % only, in its
 *  place, as Catalyst's goalkeeper radar (Ben, 2026-10-26: "1a"; the Stats tile keeps both new keys).
 *  A list that already has it keeps it once (a saved custom list). Pure. */
export function liveRadarKeys(keys: readonly string[]): string[] {
  return [...new Set(keys.map((k) => (k === "claim_success_perc" ? CLAIM_SUCCESS : k)))];
}

/** The keys a read must add so `blankNoClaimData` can work: CCAX whenever Claimable Claim Success
 *  is read (a Radar with a custom axis list may hold one without the other). Pure. */
export function claimGuardKeys(keys: readonly string[]): string[] {
  return keys.includes(CLAIM_SUCCESS) ? [CCAX] : [];
}

/**
 * No claim data → both claim rows null ("—"), never 0%. The engine divides by 0 as 0, and a
 * season the pipeline hasn't refilled (older men's) sums the new keys to 0. CCAX is a sum of
 * (attempted − expected) per claimable pass, so exactly 0 means no claimable pass faced
 * (checked 2026-10-16 on every rebuilt file: CCAX 0 ⇒ Claimable Claim Success 0, always).
 * Mutates `stats`; a map without CCAX is left alone.
 */
export function blankNoClaimData(stats: Record<string, number | null>): void {
  if (stats[CCAX] !== 0) return;
  stats[CCAX] = null;
  if (CLAIM_SUCCESS in stats) stats[CLAIM_SUCCESS] = null;
}

/** Who the player is, season-wise: every season she has, and her current league. */
export interface StatsMeta {
  /** Newest first, as `/player-seasons` lists them (club 52xxx and calendar 2xxx ids mixed). */
  seasonCodes: string[];
  current: PlayerCurrentLeague;
  /** League id → the label League Table shows ("FAWSL", or the DB name) — for the comparison
   *  strip ("… FAWSL 25/26 …"). */
  leagueLabels: Map<CompetitionId, string>;
}

export async function loadStatsMeta(
  api: LiveApi,
  playerId: PlayerId,
  snapshotLeagueNames: ReadonlyMap<CompetitionId, string>,
): Promise<StatsMeta> {
  const [{ seasons }, current, { competitions }] = await Promise.all([
    api.get<PlayerSeasonsResponse>(`/player-seasons?playerId=${playerId}`),
    api.get<PlayerCurrentLeague>(`/player-current-league?playerId=${playerId}`),
    api.get<CompetitionsResponse>("/competitions"),
  ]);
  const leagueLabels = new Map(leagueChoices(competitions, snapshotLeagueNames).map((c) => [c.id, c.label] as const));
  return { seasonCodes: seasons.map((s) => String(s.id)), current, leagueLabels };
}

/** Her "current" season: her current league's season (Bio's rule), else her newest season. */
export function currentSeasonOf(meta: StatsMeta): string {
  return meta.current.seasonId !== null ? String(meta.current.seasonId) : (meta.seasonCodes[0] ?? "");
}

/** The season to show: the one asked for if she has it, else her current one — as the
 *  snapshot hook falls back to `currentSeason`. */
export function resolveSeason(meta: StatsMeta, requested?: string): string {
  return requested !== undefined && meta.seasonCodes.includes(requested) ? requested : currentSeasonOf(meta);
}

/** A domestic competition = one with a real country (not "International") — the same signal
 *  `/player-current-league` uses (app-api db/current-league.ts): it drops the Champions League
 *  and national-team competitions, keeps leagues and domestic cups. */
export function isDomestic(c: { countryName: string | null }): boolean {
  return c.countryName !== null && c.countryName !== "International";
}

/**
 * Which league a season's numbers come from by default: her league — the league she's in now, or
 * for a past season the league she FINISHED it in (Ben, Q-007). Not "most minutes": a January
 * mover can have more minutes in her old league. Both come from `/player-current-league` (one
 * rule, app-api db/current-league.ts); a past season passes `&seasonId=`. With one domestic
 * competition that season there's nothing to decide (no call). Null = no domestic football that
 * season (e.g. a national-team summer).
 */
export async function seasonLeague(
  api: LiveApi,
  playerId: PlayerId,
  meta: StatsMeta,
  seasonCode: string,
  competitions: PlayerCompetition[],
): Promise<CompetitionId | null> {
  if (meta.current.competitionId !== null && String(meta.current.seasonId) === seasonCode) return meta.current.competitionId;
  const domestic = competitions.filter(isDomestic).map((c) => c.id);
  if (domestic.length <= 1) return domestic[0] ?? null;
  const finished = await api.get<PlayerCurrentLeague>(`/player-current-league?playerId=${playerId}&seasonId=${seasonCode}`);
  return finished.competitionId;
}

/** The dropdown's "All" — every competition she played in that season, added together (the
 *  engine sums the raw counts first, then works out the rates: combined-competition.test.ts). */
export const ALL_COMPETITIONS = "all";

/** Her competitions in one season, and which is her league (the dropdown's default). */
export interface SeasonCompetitions {
  competitions: PlayerCompetition[];
  league: CompetitionId | null;
}

export async function loadSeasonCompetitions(
  api: LiveApi,
  playerId: PlayerId,
  meta: StatsMeta,
  seasonCode: string,
): Promise<SeasonCompetitions> {
  const { competitions } = await api.get<PlayerCompetitionsResponse>(
    `/player-competitions?playerId=${playerId}&seasonId=${seasonCode}`,
  );
  const league = await seasonLeague(api, playerId, meta, seasonCode, competitions);
  // Her league is always one of her competitions that season — if not, the two routes disagree
  // and the dropdown would show a choice it can't list.
  if (league !== null && !competitions.some((c) => c.id === league)) {
    throw new Error(`League ${league} isn't among player ${playerId}'s competitions in ${seasonCode}`);
  }
  return { competitions, league };
}

/**
 * The dropdown's choice to show: the one asked for if she played in it that season ("all"
 * always counts), else her league. A season with no league (only internationals) → "all", so
 * she still has numbers to show.
 */
export function resolveCompetition(sc: SeasonCompetitions, requested: string | null | undefined): string {
  if (requested === ALL_COMPETITIONS) return ALL_COMPETITIONS;
  if (requested && sc.competitions.some((c) => String(c.id) === requested)) return requested;
  return sc.league !== null ? String(sc.league) : ALL_COMPETITIONS;
}

/** The competition ids a choice covers. */
export function competitionIdsOf(sc: SeasonCompetitions, choice: string): CompetitionId[] {
  return choice === ALL_COMPETITIONS ? sc.competitions.map((c) => c.id) : [Number(choice)];
}

/** One dropdown line: the full name in the open list, the short one on the closed button. */
export interface CompetitionOption {
  value: string;
  label: string;
  short: string;
}

/** Her league first, her other competitions in the API's order, "All" last. */
export function competitionOptions(sc: SeasonCompetitions): CompetitionOption[] {
  const ordered = [
    ...sc.competitions.filter((c) => c.id === sc.league),
    ...sc.competitions.filter((c) => c.id !== sc.league),
  ];
  return [
    ...ordered.map((c) => ({ value: String(c.id), label: c.name, short: shortCompetitionName(c.name) })),
    { value: ALL_COMPETITIONS, label: "All competitions", short: "All" },
  ];
}

/** One `groupBy: player_id` row → the snapshot's flat key → value map, under the snapshot's own
 *  keys. No row (she has no minutes there) → {} — the widget's "No stats recorded". */
export function toStatsMap(rows: PlayerStatsRow[], snapshotKeys: string[]): Record<string, number | null> {
  if (rows.length > 1) throw new Error(`Expected one player row, got ${rows.length}`);
  const row = rows[0];
  if (!row) return {};
  const out: Record<string, number | null> = {};
  for (const key of [...snapshotKeys, NINETIES]) {
    if (HIDDEN_LIVE.has(key)) {
      out[key] = null;
      continue;
    }
    const value = row[engineKeyOf(key)];
    if (typeof value === "string") throw new Error(`Metric ${key} came back as text: ${value}`);
    out[key] = value ?? null;
  }
  blankNoClaimData(out);
  return out;
}

/** A live tile's season: the snapshot's shape (what the widget reads) plus where the numbers
 *  come from — what Stats-2 needs to find her comparison group. */
export interface LiveSeasonStats extends ProtoPlayerStats {
  /** Her league, when the numbers are her league's alone (fork 2A — the dropdown's default);
   *  null for another competition, "All", or a season with no league. Stats-2's group needs it. */
  competitionId: CompetitionId | null;
  /** That league's label ("FAWSL"); null with no league. */
  leagueLabel: string | null;
  /** Her minutes there (all positions); null with no league or no row. */
  minutes: number | null;
}

/** Her minutes from the stats row — the group's 150-minute floor is checked against it. */
function minutesOf(rows: PlayerStatsRow[]): number | null {
  const minutes = rows[0]?.minutes;
  if (minutes === undefined || minutes === null) return null;
  if (typeof minutes !== "number") throw new Error(`Minutes came back as text: ${minutes}`);
  return minutes;
}

/** Her minutes by position in those competitions — the Goalkeeping tile's gate (Bio's rule). */
function minutesByPosition(playerId: PlayerId, competitionIds: CompetitionId[], seasonId: number): PlayerStatsRequest {
  return {
    keys: ["minutes"],
    where: { player_id: [playerId], competition_id: competitionIds, season_id: [seasonId] },
    groupBy: ["position_id"],
  };
}

/**
 * One tile's values for one season and the dropdown's choice (one competition, or all of hers):
 * the tile's own metric keys (≤ 52) in one call, plus her position there (the Goalkeeping gate).
 * No competitions (no football recorded that season) → no stats, no call.
 */
export async function loadSeasonStats(
  api: LiveApi,
  playerId: PlayerId,
  meta: StatsMeta,
  seasonCode: string,
  sc: SeasonCompetitions,
  choice: string,
  snapshotKeys: string[],
): Promise<LiveSeasonStats> {
  const base = { seasonCodes: meta.seasonCodes, seasonCode, currentSeason: currentSeasonOf(meta) };
  const competitionIds = competitionIdsOf(sc, choice);
  if (competitionIds.length === 0) {
    return { ...base, stats: {}, positionTemplate: null, competitionId: null, leagueLabel: null, minutes: null };
  }
  const competitionId = sc.league !== null && choice === String(sc.league) ? sc.league : null;
  const leagueLabel = competitionId === null ? null : (meta.leagueLabels.get(competitionId) ?? null);
  if (competitionId !== null && leagueLabel === null) {
    throw new Error(`Player ${playerId}: league ${competitionId} isn't in /competitions`);
  }
  const seasonId = Number(seasonCode);
  const [stats, positions] = await Promise.all([
    api.post<PlayerStatsResponse>("/player-stats", {
      // `minutes` rides along for the group's floor (Stats-2); the tile doesn't show it.
      // Hidden keys stay out of the request: the engine may no longer know them (v1.7.0 dropped
      // `claim_success_perc`), and one unknown key is a 400 for the whole tile.
      keys: [...new Set([...snapshotKeys.filter((k) => !HIDDEN_LIVE.has(k)), NINETIES, "minutes"].map(engineKeyOf))],
      where: { player_id: [playerId], competition_id: competitionIds, season_id: [seasonId] },
      groupBy: ["player_id"],
    }),
    api.post<PlayerStatsResponse>("/player-stats", minutesByPosition(playerId, competitionIds, seasonId)),
  ]);
  return {
    ...base,
    stats: toStatsMap(stats.rows, snapshotKeys),
    positionTemplate: positionTemplateOf(positions.rows),
    competitionId,
    leagueLabel,
    minutes: minutesOf(stats.rows),
  };
}
